import {
  GetProgrammaticCampaignAccountsLiftRO,
  GetProgrammaticCampaignStatisticsRO,
} from "@intentsify/dto";
import { decimalToPercentage, toNumberDisplayValue } from "@intentsify/utils";
import { useQuery } from "@tanstack/react-query";
import { StatCardsContainer } from "components";
import { FaHashtag } from "react-icons/fa";
import { DisplayPerformanceErrorBoundary } from "../../../../DisplayPerformanceErrorBoundary";
import { DisplayPerformanceFilters } from "../../../../types";
import { NOT_A_CPA_CAMPAIGN_MESSAGE } from "../../../../../displayPerformance.utils";

type AccountStatCardsProps = {
  campaignId: string;
  filters: DisplayPerformanceFilters;
  getAccountLifts: (
    id: string,
    filters: DisplayPerformanceFilters
  ) => Promise<GetProgrammaticCampaignAccountsLiftRO>;
  getStatistics: (
    id: string,
    filters: DisplayPerformanceFilters
  ) => Promise<GetProgrammaticCampaignStatisticsRO>;
  isBasicView?: boolean;
};

const TITLE = "Account Statistics";

const AccountStatCards = (props: AccountStatCardsProps) => (
  <DisplayPerformanceErrorBoundary cardTitle={TITLE}>
    <AccountStatCardsContent {...props} />
  </DisplayPerformanceErrorBoundary>
);

const AccountStatCardsContent = ({
  campaignId,
  filters,
  getAccountLifts,
  getStatistics,
  isBasicView,
}: AccountStatCardsProps) => {
  const { data: dataLifts, isFetching: isLoadingLifts } = useQuery(
    ["getAccountLifts", campaignId, filters],
    () => getAccountLifts(campaignId, filters)
  );

  const { data: statisticsData } = useQuery(
    ["impressionsClicksPerformance-statistics", campaignId, filters],
    () => getStatistics(campaignId, filters)
  );

  return (
    <StatCardsContainer
      items={[
        ...(!isBasicView
          ? [
              {
                isLoading: isLoadingLifts,
                title: "Target Accounts",
                value: dataLifts?.targetAccounts,
                valueFormatter: (value: number) => toNumberDisplayValue(value),
                titleTooltip:
                  "Total list of accounts Intentsify monitors for intent activity and programmatic targeting.",
              },
            ]
          : []),
        {
          isLoading: isLoadingLifts,
          title: "Accounts Reached",
          value: dataLifts?.accountsReached,
          previousPeriodChange: dataLifts?.accountsReachedPreviousPeriod,
          valueFormatter: (value: number) => toNumberDisplayValue(value),
          titleTooltip:
            "Accounts that have been served an ad programmatically.",
        },
        {
          isLoading: isLoadingLifts,
          title: "Accounts Engaged",
          value: dataLifts?.accountsEngaged,
          previousPeriodChange: dataLifts?.accountsEngagedPreviousPeriod,
          valueFormatter: (value: number) => toNumberDisplayValue(value),
          titleTooltip: "Accounts that have interacted with a programmatic ad.",
        },
        {
          isLoading: isLoadingLifts,
          title: "Account Engagement Rate",
          value: dataLifts?.accountEngagementRate,
          previousPeriodChange: dataLifts?.accountEngagementRatePreviousPeriod,
          valueFormatter: (value: number) =>
            `${toNumberDisplayValue(decimalToPercentage(value))}%`,
          titleTooltip: "The rate at which accounts have engaged with an ad.",
        },
        {
          isLoading: isLoadingLifts,
          title: "Accounts With Conversions",
          value: dataLifts?.accountsWithConversion,
          previousPeriodChange: dataLifts?.accountsWithConversionPreviousPeriod,
          valueFormatter: (value: number) => toNumberDisplayValue(value),
          titleTooltip: "An account that has converted based on the KPI.",
          hasData: statisticsData?.totalConversions !== 0,
          noDataMessage: NOT_A_CPA_CAMPAIGN_MESSAGE,
          noDataIcon: FaHashtag,
        },
      ]}
    />
  );
};

export { AccountStatCards };
